// Ficha del visitante / persona — una línea de tiempo:
//   fuente → páginas → eventos → registro (etapa) → pagos → conversación.
// Fuentes (org SIEMPRE del JWT):
//   · mcp_django GET /admin/public-tracking/journey (visitor_id | lead_id)
//   · Orión GET /visitor-threads (por visitor_id del chat del sitio y/o teléfono del lead)
// Se abre desde «Resultados de campaña» (lista de visitantes) y desde la ficha del lead (CRM).
// Tokens semánticos (claro/oscuro).
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Globe, MousePointerClick, UserPlus, CreditCard, BadgeCheck, MessagesSquare, Eye } from 'lucide-react';
import { fetchJourney, type Journey, type JourneyItem } from '@/modules/public-tracking/api';
import { getVisitorThreads, type VisitorThread } from '@/lib/api';

export type JourneyLoaders = {
  journey: (q: { visitor_id?: string; lead_id?: string }) => Promise<Journey>;
  threads: (q: { visitor_id?: string; phones?: string[] }) => Promise<{ threads: VisitorThread[] } | null>;
};

export const defaultJourneyLoaders: JourneyLoaders = { journey: fetchJourney, threads: getVisitorThreads };

const TZ = 'America/Mexico_City';
const when = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-MX', { timeZone: TZ, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};
const money = (n: number, cur = 'MXN') => {
  try {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: cur || 'MXN', maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return `${n} ${cur}`;
  }
};
const CHANNEL: Record<string, string> = {
  whatsapp: 'WhatsApp', facebook: 'Facebook', instagram: 'Instagram', google: 'Google', linkedin: 'LinkedIn',
  tiktok: 'TikTok', email: 'Correo', directo: 'Directo', otro: 'Otro sitio', web: 'Chat del sitio', voice_phone: 'Teléfono',
};
const ORDER_STATUS: Record<string, string> = {
  paid: 'pagado', checkout_open: 'en pago', awaiting_payment: 'esperando OXXO', expired: 'vencido', canceled: 'cancelado', refunded: 'reembolsado',
};

function icon(it: JourneyItem) {
  const c = 'h-3.5 w-3.5';
  if (it.kind === 'source') return <Globe className={c} />;
  if (it.kind === 'lead') return <UserPlus className={c} />;
  if (it.kind === 'order') return <CreditCard className={c} />;
  if (it.kind === 'payment') return <BadgeCheck className={c} />;
  if (it.event_type === 'pageview') return <Eye className={c} />;
  if (it.event_type === 'chat_message' || it.event_type === 'whatsapp_click') return <MessagesSquare className={c} />;
  return <MousePointerClick className={c} />;
}

function detailText(it: JourneyItem): string {
  const d = it.detail || {};
  if (it.kind === 'source') {
    return [CHANNEL[d.channel] || d.channel, d.utm_campaign && `campaña ${d.utm_campaign}`, d.utm_medium, d.referrer_host, d.landing_path]
      .filter(Boolean).join(' · ');
  }
  if (it.kind === 'lead') return [d.lifecycle_stage && `etapa ${d.lifecycle_stage}`, d.status && `estado ${d.status}`].filter(Boolean).join(' · ');
  if (it.kind === 'order') return `${ORDER_STATUS[d.status] || d.status} · ${money(d.amount, d.currency)} · ${d.seats} lugar(es)`;
  if (it.kind === 'payment') return `${money(d.amount, d.currency)} · ${d.seats} lugar(es)`;
  return [it.path, d.label, d.form].filter(Boolean).join(' · ');
}

interface Props {
  visitorId?: string;
  leadId?: string;
  onClose: () => void;
  loaders?: JourneyLoaders;
}

export const JourneyDrawer: React.FC<Props> = ({ visitorId, leadId, onClose, loaders = defaultJourneyLoaders }) => {
  const [data, setData] = useState<Journey | null>(null);
  const [threads, setThreads] = useState<VisitorThread[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setData(null);
    setThreads(null);
    setError('');
    loaders
      .journey({ visitor_id: visitorId, lead_id: leadId })
      .then(async (j) => {
        if (!alive) return;
        setData(j);
        const t = await loaders.threads({ visitor_id: j.conversation_lookup.visitor_id || undefined, phones: j.conversation_lookup.phones }).catch(() => null);
        if (alive) setThreads(t ? t.threads : []);
      })
      .catch((e: any) => alive && setError(/404/.test(String(e?.message)) || /no encontrado/i.test(String(e?.message))
        ? 'No hay recorrido medido para esta persona (llegó antes de medir visitas, desde otro dispositivo o con «no rastrear»).'
        : e?.message || 'No se pudo cargar la ficha.'));
    return () => {
      alive = false;
    };
  }, [visitorId, leadId, loaders]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const v = data?.visitor;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={onClose} role="dialog" aria-modal="true" aria-label="Ficha del visitante">
      <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-border bg-background text-foreground shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-background/95 px-5 py-3 backdrop-blur">
          <div className="min-w-0">
            <h3 className="font-semibold truncate">{data?.leads[0]?.name || data?.leads[0]?.email || 'Visitante anónimo'}</h3>
            <p className="text-xs text-muted-foreground truncate">
              {v ? `Visitante ${v.visitor_id.slice(0, 8)}… · desde ${when(v.first_seen)}` : leadId ? 'Ficha del lead' : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="p-2 rounded hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
        </header>

        <div className="p-5 space-y-5">
          {error && <div className="rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground">{error}</div>}
          {!data && !error && <div className="text-sm text-muted-foreground">Cargando ficha…</div>}

          {data && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Box title="Fuente">
                  {v ? (
                    <>
                      <div className="font-medium">{CHANNEL[v.source.channel] || v.source.channel}</div>
                      <div className="text-xs text-muted-foreground break-words">
                        {[v.source.utm_campaign, v.attribution.utm_content, v.source.utm_medium].filter(Boolean).join(' · ') || v.source.referrer_host || 'sin UTM'}
                      </div>
                    </>
                  ) : <span className="text-muted-foreground">sin visita medida</span>}
                </Box>
                <Box title="Páginas vistas">
                  <div className="font-medium tabular-nums">{data.pages.length}</div>
                  <div className="text-xs text-muted-foreground truncate" title={data.pages.join(', ')}>{data.pages.slice(0, 3).join(', ') || '—'}</div>
                </Box>
                <Box title="Registro">
                  {data.leads.length ? data.leads.map((l) => (
                    <div key={l.id} className="text-xs"><span className="font-medium text-sm">{l.source.replace(/_/g, ' ')}</span><br />
                      <span className="text-muted-foreground">etapa {l.lifecycle_stage || '—'} · {l.status}</span></div>
                  )) : <span className="text-muted-foreground">sin registro</span>}
                </Box>
                <Box title="Pagos">
                  {data.orders.length ? data.orders.map((o) => (
                    <div key={o.id} className="text-xs"><span className="font-medium text-sm">{money(o.amount, o.currency)}</span>{' '}
                      <span className="text-muted-foreground">{ORDER_STATUS[o.status] || o.status}</span></div>
                  )) : <span className="text-muted-foreground">sin pagos</span>}
                </Box>
              </div>

              <section>
                <h4 className="text-sm font-semibold">Conversaciones</h4>
                {threads === null ? (
                  <p className="mt-2 text-xs text-muted-foreground">Buscando en Orión…</p>
                ) : threads.length ? (
                  <ul className="mt-2 space-y-2">
                    {threads.map((t) => (
                      <li key={t.id} className="rounded-lg border border-border p-3 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">{CHANNEL[t.channel] || t.channel}</span>
                          <span className="text-xs text-muted-foreground">{when(t.last_message_at)}</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{t.last_message_preview || t.title || '—'}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground">{t.matched_by === 'visitor_id' ? 'Mismo visitante del sitio' : 'Mismo teléfono del registro'}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">Sin conversaciones vinculadas (ni del chat del sitio ni por su teléfono).</p>
                )}
              </section>

              <section>
                <h4 className="text-sm font-semibold">Línea de tiempo</h4>
                <ol className="mt-3 relative border-l border-border ml-2 space-y-3">
                  {data.timeline.map((it, i) => (
                    <li key={i} className="ml-4">
                      <span className="absolute -left-[9px] mt-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-border bg-card text-primary">{icon(it)}</span>
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                        <span className="text-sm font-medium">{it.label}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{when(it.ts)}</span>
                      </div>
                      {detailText(it) && <p className="text-xs text-muted-foreground break-words">{detailText(it)}</p>}
                    </li>
                  ))}
                </ol>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

const Box: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-lg border border-border bg-card p-3 min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{title}</div>
    {children}
  </div>
);

export default JourneyDrawer;
